import { useEffect, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { mutate } from "swr";

import useAccountChildren from "../../../../../hooks/useAccountChildren";
import useCompetition, {
  useCompetitionRoutes,
  useCompetitionScorecard,
} from "../../../../../hooks/useCompetition";
import useCurrentUser from "../../../../../hooks/useCurrentUser";
import { getIsLoggedIn } from "../../../../../services/auth";
import { CompetitorByNumberData } from "../../../../onboarding/components/screens/CompetitorNumberScreen/useCompetitorNumberScreen";
import getCanSeeLeaderboard from "../../../../../utils/getCanSeeLeaderboard";

export default function useScorecardScreen() {
  const { children } = useAccountChildren();
  const { profile } = useCurrentUser();

  const [searchParams, setSearchParams] = useSearchParams();
  const params = useParams();

  const { competitionUUID } = params;
  const { competition } = useCompetition(competitionUUID);

  const navigate = useNavigate();
  useEffect(() => {
    if (
      competition &&
      !getIsLoggedIn() &&
      !competition?.settings.hasPreloadedEntries
    ) {
      navigate(`/${competitionUUID}/enter`, { replace: true });
    }
    // Disabled to run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [competition]);

  const userUUID = searchParams.get("entry") || "me";

  const { entry, isValidating: entryLoading } = useCompetitionScorecard({
    userUUID,
    competitionUUID,
  });

  useEffect(() => {
    if (
      competition &&
      !competition.userEntry &&
      competition.childEntries?.[0]
    ) {
      setSearchParams({ entry: competition?.childEntries?.[0].user.uuid });
    }

    if (
      competition &&
      !competition.userEntry &&
      competition.settings.hasPreloadedEntries
    ) {
      const items: CompetitorByNumberData[] = JSON.parse(
        localStorage.getItem(`${competition.uuid}/competitorNumberEntries`) ||
          "[]"
      );
      if (!items[0]) {
        navigate(`/${competition.uuid}/enter/competitor-number`, {
          replace: true,
        });
      } else {
        setSearchParams({ entry: items[0].uuid });
      }
    }
  }, [competition, setSearchParams, navigate]);

  const availableEntries = useMemo(() => {
    if (competition?.settings.hasPreloadedEntries) {
      const items: CompetitorByNumberData[] = JSON.parse(
        localStorage.getItem(`${competitionUUID}/competitorNumberEntries`) ||
          "[]"
      );
      return items.map((item) => ({
        user: {
          firstName: item.firstName,
          lastName: item.lastName,
          uuid: item.uuid,
        },
      }));
    }
    if (!profile || !competition) {
      return [];
    }
    return [
      ...(competition?.userEntry
        ? [{ ...competition.userEntry, user: profile }]
        : []),
      ...(competition?.childEntries || []),
    ];
  }, [competition, profile, competitionUUID]);

  const selectEntry = (uuid: string) =>
    setSearchParams({ entry: uuid }, { replace: true });

  const reload = () =>
    mutate(`/competitions/${competitionUUID}/entries/${userUUID}`);

  const { routes } = useCompetitionRoutes({
    venueUUID: competition?.venues?.[0]?.uuid,
    competitionUUID,
  });

  const entryRoutes = useMemo(() => {
    return entry?.category?.uuid
      ? routes?.filter((route) =>
          route.competitionRouteGroups.find((routeGroup) =>
            routeGroup.categories?.includes(entry.category.uuid)
          )
        )
      : undefined;
  }, [routes, entry]);

  const onAddCompetitorNumber = () => navigate("./enter/competitor-number");

  const canViewLeaderboard = competition && getCanSeeLeaderboard(competition);

  const navigateToLeaderboard = () => navigate(`/${competitionUUID}/entries`);

  return {
    entry,
    entryLoading,
    entryRoutes,
    children,
    availableEntries,
    selectEntry,
    reload,
    competition,
    onAddCompetitorNumber,
    canViewLeaderboard,
    navigateToLeaderboard,
  };
}
